/**
 * Təsdiq və giriş dialoqu.
 *
 * NİYƏ LAZIMDIR
 * Sistemdə 9 yerdə `window.confirm` / `window.prompt` işlədilirdi. Onlar
 * işləyir, amma:
 *   • brauzerin öz görünüşünü daşıyır — məhsul kimi görünmür,
 *   • MƏTNDƏN BAŞQA heç nə göstərə bilmir: «20 məktub silinəcək»
 *     xəbərdarlığını qabartmaq, təhlükəli düyməni qırmızı etmək mümkün deyil,
 *   • bəzi brauzerlərdə «bu sayt bir daha dialoq göstərməsin» qutusu çıxır
 *     və istifadəçi onu işarələsə TƏSDİQ TAMAMİLƏ İTİR — silmə səssizcə
 *     baş verir və ya heç vaxt baş vermir.
 *
 * ⚠️ ƏLÇATANLIQ ƏL İLƏ QURULUR — brauzer dialoqu bunu pulsuz verirdi.
 * Ona görə burada hamısı var: `role="dialog"`, `aria-modal`, fokus tələsi,
 * Esc ilə bağlanma, açılanda fokusun içəri keçməsi, bağlananda geri
 * qayıtması. Bunlardan biri olmasa, komponent brauzer dialoqundan PİS olur.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';

export interface DialogSpec {
  title: string;
  /** İzah — nə baş verəcək. Təhlükəli əməliyyatda NƏTİCƏ yazılmalıdır. */
  body?: string;
  /** Təsdiq düyməsinin adı. Susmaya görə «Təsdiqlə». */
  confirmLabel?: string;
  /** `true` — düymə qırmızı olur və fokus «Ləğv et»də başlayır. */
  danger?: boolean;
  /**
   * Verilirsə dialoq mətn girişi göstərir (köhnə `window.prompt` əvəzi).
   * Təsdiqdə daxil edilən dəyər qaytarılır.
   */
  input?: { label: string; placeholder?: string; initial?: string };
}

interface Props {
  spec: DialogSpec;
  /** `value` yalnız `input` verilibsə dolu olur. */
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function Dialog({ spec, onConfirm, onCancel }: Props): React.ReactElement {
  const [value, setValue] = useState(spec.input?.initial ?? '');
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  /*
    ⚠️ `onCancel` REF-DƏ SAXLANILIR, ASILILIQ SİYAHISINDA DEYİL.
    `useDialog` onu hər render-də yenidən yaradır; asılılığa qoysaq effekt
    hər valideyn render-ində təkrar işləyir və:
      • `returnTo` yenidən tutulur (artıq dialoqun içindəki düymə olur,
        yəni bağlananda fokus SƏHV yerə qayıdır),
      • ilkin fokus yenidən qoyulur — istifadəçi mətn yazarkən kursor
        düyməyə tullanır.
    Effekt YALNIZ bir dəfə, açılışda işləməlidir.
  */
  const cancelRef2 = useRef(onCancel);
  cancelRef2.current = onCancel;

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;

    // ⚠️ TƏHLÜKƏLİ ƏMƏLİYYATDA FOKUS «LƏĞV ET»DƏ BAŞLAYIR.
    // Enter-ə basmağa öyrəşmiş adam təsdiq düyməsi fokusda olsa məktubunu
    // təsadüfən həmişəlik silərdi. Giriş sahəsi varsa fokus ona keçir —
    // orada məqsəd yazmaqdır, silmək yox.
    if (spec.input) inputRef.current?.focus();
    else if (spec.danger) cancelRef.current?.focus();
    else confirmRef.current?.focus();

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRef2.current();
        return;
      }
      // ── Fokus tələsi ────────────────────────────────────────────────
      // ⚠️ BUNSUZ Tab fokusu dialoqun ARXASINDAKI səhifəyə aparır və
      // istifadəçi görünməyən elementlərlə işləyir. Modal olmağın bütün
      // mənası fokusun içəridə qalmasıdır.
      if (e.key !== 'Tab') return;
      const focusable = boxRef.current?.querySelectorAll<HTMLElement>(
        'button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Fokus çağıran düyməyə qaytarılır — klaviatura istifadəçisi
      // dialoqdan sonra siyahının başına atılmamalıdır.
      returnTo?.focus?.();
    };
    // ⚠️ BOŞ ASILILIQ — QƏSDƏN. Yuxarıdakı izaha bax.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (spec.input && !value.trim()) return;
    onConfirm(value.trim());
  }

  return (
    <div
      className="dlg-scrim"
      // ⚠️ FONA KLİK BAĞLAYIR, amma yalnız FONA — dialoqun özünə klik yox.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="dlg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dlg-title"
        {...(spec.body ? { 'aria-describedby': 'dlg-body' } : {})}
        ref={boxRef}
      >
        <form onSubmit={submit}>
          <h2 className="dlg-title" id="dlg-title">
            {spec.title}
          </h2>
          {spec.body && (
            <p className="dlg-body" id="dlg-body">
              {spec.body}
            </p>
          )}

          {spec.input && (
            <label className="field">
              <span>{spec.input.label}</span>
              <input
                ref={inputRef}
                value={value}
                placeholder={spec.input.placeholder ?? ''}
                onChange={(e) => setValue(e.target.value)}
              />
            </label>
          )}

          <div className="dlg-actions">
            {/*
              ⚠️ «LƏĞV ET» SOLDA, TƏSDİQ SAĞDA — və təsdiq son elementdir.
              Fokus tələsi sonuncu elementə qayıtdığı üçün sıra əhəmiyyətlidir.
            */}
            <button type="button" className="btn-text" onClick={onCancel} ref={cancelRef}>
              Ləğv et
            </button>
            <button
              type="submit"
              className={spec.danger ? 'btn-primary is-danger' : 'btn-primary'}
              ref={confirmRef}
              disabled={Boolean(spec.input) && !value.trim()}
            >
              {spec.confirmLabel ?? 'Təsdiqlə'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Dialoqu vəd (Promise) kimi işlətmək üçün kiçik köməkçi.
 *
 * ⚠️ NİYƏ VƏD: `window.confirm` sinxron idi və kod belə yazılmışdı:
 *     if (!window.confirm(...)) return;
 * Vəd qaytarmaqla eyni forma saxlanılır:
 *     if (!(await ask({...}))) return;
 * yəni çağıran yerlərdə məntiq quruluşu dəyişmir — yalnız `await` əlavə
 * olunur. Bu, 9 yerin hər birini yenidən yazmaqdan qat-qat təhlükəsizdir.
 */
export function useDialog(): {
  node: React.ReactElement | null;
  ask: (spec: DialogSpec) => Promise<string | null>;
} {
  const [state, setState] = useState<{
    spec: DialogSpec;
    resolve: (v: string | null) => void;
  } | null>(null);

  const ask = (spec: DialogSpec): Promise<string | null> =>
    new Promise((resolve) => setState({ spec, resolve }));

  const node = state ? (
    <Dialog
      spec={state.spec}
      onConfirm={(v) => {
        state.resolve(v);
        setState(null);
      }}
      onCancel={() => {
        state.resolve(null);
        setState(null);
      }}
    />
  ) : null;

  return { node, ask };
}
